import classNames from 'classnames';
import { ReactNode, useLayoutEffect, useRef } from 'react';
import { Scrollbars } from 'react-custom-scrollbars-4';

import css from './CustomScrollbar.css';

/** Scrollbars instance exposes the scrollable view element (not in lib types). */
type ScrollbarsWithView = Scrollbars & { view?: HTMLDivElement };

interface CustomScrollbarProps {
  children: ReactNode;
  autoHeight?: boolean;
  autoHeightMax?: number;
  className?: string;
  trackClassName?: string;
  viewClassName?: string;
  viewRef?: React.Ref<HTMLDivElement>;
}

const CustomScrollbar = ({
  children,
  className,
  trackClassName,
  viewClassName,
  viewRef,
  autoHeightMax,
  autoHeight = false,
}: CustomScrollbarProps) => {
  const scrollbarsRef = useRef<ScrollbarsWithView>(null);

  /**
   * Workaround: react-custom-scrollbars-2 applies its own ref to the view via cloneElement,
   * so we can't pass viewRef in renderView (it would be overwritten). Forward the scrollable
   * view element by reading the library's internal .view from the Scrollbars instance (e.g.
   * for react-select so keyboard navigation can scroll the focused option into view).
   */
  useLayoutEffect(() => {
    if (viewRef) {
      const view = scrollbarsRef.current?.view ?? null;

      if (typeof viewRef === 'function') {
        viewRef(view);
        return () => viewRef(null);
      }

      (viewRef as React.MutableRefObject<HTMLDivElement | null>).current = view;
      return () => ((viewRef as React.MutableRefObject<HTMLDivElement | null>).current = null);
    }
  }, [viewRef]);

  return (
    <Scrollbars
      ref={scrollbarsRef}
      className={classNames(css.container, className)}
      autoHeight={autoHeight}
      autoHeightMax={autoHeightMax}
      renderTrackVertical={(props) => <div {...props} className={classNames(css.trackY, trackClassName)} />}
      renderThumbVertical={(props) => <div {...props} className={css.thumbY} />}
      renderView={(props) => <div {...props} className={viewClassName} />}
      universal
    >
      {children}
    </Scrollbars>
  );
};

export default CustomScrollbar;
